import { useVerifyAuthenticatorSetupMutation } from '@codezo/graphql';
import { useThemeColors } from '@codezo/theme';
import { formatDateTime } from '@codezo/ui';
import { Alert, Box, Button, Image, PinInput, Stack, Text } from '@mantine/core';
import { useForm } from '@mantine/form';
import { IconAlertCircle, IconCheck } from '@tabler/icons-react';
import { useCallback, useEffect, useRef, useState } from 'react';

interface AuthenticatorSetupStepProps {
	data: Record<string, unknown>;
	onDataChange: (_data: Record<string, unknown>) => void;
	onValidSubmit: (_data: Record<string, unknown>) => void;
	onPrevious: () => void;
	isFirstStep: boolean;
	isLastStep: boolean;
	triggerValidation: number;
}

export function AuthenticatorSetupStep({
	data,
	onDataChange,
	onValidSubmit,
	triggerValidation,
}: AuthenticatorSetupStepProps) {
	const lastTriggerRef = useRef(0);
	const [isVerifying, setIsVerifying] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [backupCodes, setBackupCodes] = useState<string[]>([]);
	const [setupComplete, setSetupComplete] = useState(false);
	const [hasDownloaded, setHasDownloaded] = useState(false);
	const [verifyAuthenticatorSetup] = useVerifyAuthenticatorSetupMutation();
	const colors = useThemeColors();

	const form = useForm({
		initialValues: {
			verificationCode: '',
		},
		validate: {
			verificationCode: (value) => {
				if (!value.trim()) return 'Verification code is required';
				if (!/^\d{6}$/.test(value.trim())) return 'Code must be 6 digits';
				return null;
			},
		},
	});

	const handleSubmit = useCallback(
		async (values: typeof form.values) => {
			setIsVerifying(true);
			setError(null);

			try {
				const { data: verifyData } = await verifyAuthenticatorSetup({
					variables: {
						input: {
							userId: data.userId as string,
							token: values.verificationCode,
						},
					},
				});

				if (verifyData?.verifyAuthenticatorSetup) {
					const { token, backupCodes: codes } = verifyData.verifyAuthenticatorSetup;

					// Store backup codes and mark setup as complete
					if (codes) {
						setBackupCodes(codes);
					}
					setSetupComplete(true);

					// Store auth token in data for later use
					onDataChange({
						...data,
						authToken: token,
						backupCodes: codes,
					});
				}
			} catch {
				// Error handled by mutation onError
				setError('Invalid verification code. Please try again.');
			} finally {
				setIsVerifying(false);
			}
		},
		[verifyAuthenticatorSetup, data, onDataChange, form]
	);

	const handleDownloadBackupCodes = () => {
		const content = `Two-Factor Authentication Backup Codes\n\nSave these codes in a secure place. Each code can only be used once.\n\n${backupCodes.join('\n')}\n\nGenerated: ${formatDateTime(new Date())}`;
		const blob = new Blob([content], { type: 'text/plain' });
		const url = URL.createObjectURL(blob);
		const a = document.createElement('a');
		a.href = url;
		a.download = 'backup-codes.txt';
		document.body.appendChild(a);
		a.click();
		document.body.removeChild(a);
		URL.revokeObjectURL(url);
		setHasDownloaded(true);
	};

	const handleContinueAfterBackup = useCallback(() => {
		// Pass completion to parent - user can continue even without downloading
		onValidSubmit({
			...form.values,
			mfaSetupComplete: true,
			authToken: data.authToken,
			backupCodes,
		});
	}, [onValidSubmit, form.values, data.authToken, backupCodes]);

	// Update parent component whenever form values change
	useEffect(() => {
		onDataChange(form.values);
	}, [form.values, onDataChange]);

	// Handle validation trigger from parent
	useEffect(() => {
		if (triggerValidation > 0 && triggerValidation !== lastTriggerRef.current) {
			lastTriggerRef.current = triggerValidation;

			// If setup is complete, continue
			if (setupComplete) {
				handleContinueAfterBackup();
				return;
			}

			const validationResult = form.validate();

			if (!validationResult.hasErrors) {
				handleSubmit(form.values);
			}
		}
	}, [triggerValidation, form, handleSubmit, setupComplete, handleContinueAfterBackup]);

	return (
		<Box>
			<form onSubmit={form.onSubmit(handleSubmit)}>
				<Stack gap="lg">
					{Boolean(data.qrCodeDataURL) && !setupComplete && (
						<Stack align="center" gap="md">
							<Text size="sm" c={colors.textMuted} ta="center">
								Scan this QR code with your authenticator app:
							</Text>

							<Box
								p="md"
								style={{
									backgroundColor: 'white',
									borderRadius: 8,
								}}
							>
								<Image
									src={data.qrCodeDataURL as string}
									width={200}
									height={200}
									alt="MFA QR Code"
								/>
							</Box>
						</Stack>
					)}

					{!setupComplete ? (
						/* Verification Input */
						<Stack align="center" gap="md">
							<Text size="sm" c={colors.textMuted} ta="center">
								Enter the 6-digit code from your authenticator app
							</Text>

							{error && (
								<Alert
									icon={<IconAlertCircle size={20} color={colors.error} />}
									color="error"
									style={{ width: '100%' }}
								>
									{error}
								</Alert>
							)}

							<PinInput
								length={6}
								type="number"
								size="xl"
								value={form.values.verificationCode}
								onChange={(value) => form.setFieldValue('verificationCode', value)}
								disabled={isVerifying}
								onComplete={() => handleSubmit(form.values)}
								error={!!error}
								autoFocus
							/>

							{isVerifying && (
								<Text size="sm" c={colors.textMuted}>
									Verifying...
								</Text>
							)}
						</Stack>
					) : (
						/* Success State with Backup Codes Download */
						<Stack gap="md" align="center">
							<Alert
								icon={<IconCheck size={20} />}
								color="green"
								style={{ width: '100%' }}
							>
								Two-factor authentication has been successfully enabled!
							</Alert>

							<Text size="sm" c={colors.textMuted} ta="center">
								Save your backup codes before continuing. You&apos;ll need them if
								you lose access to your authenticator.
							</Text>

							<Button
								fullWidth
								onClick={handleDownloadBackupCodes}
								size="sm"
								variant={hasDownloaded ? 'light' : 'filled'}
								leftSection={hasDownloaded ? <IconCheck size={16} /> : null}
							>
								{hasDownloaded
									? 'Backup Codes Downloaded'
									: 'Download Backup Codes'}
							</Button>
						</Stack>
					)}
				</Stack>
			</form>
		</Box>
	);
}
